import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { toast } from 'sonner';
import './App.css';
import AttendanceManagement from './components/AttendanceManagement';
import CourseManagement from './components/CourseManagement';
import CRMManagement from './components/crm/CRMManagement';
import Dashboard from './components/Dashboard';
import EnquiryForm from './components/EnquiryForm';
import FeeManagement from './components/FeeManagement';
import Layout from './components/Layout';
import LeadStatusPage from './components/LeadStatusPage';
import LoginPage from './components/LoginPage';
import StaffManagement from './components/StaffManagement';
import StudentManagement from './components/StudentManagement';
import StudentProfile from './components/StudentProfile';
import TrainerManagement from './components/TrainerManagement';
import BatchAnalytics from './components/BatchAnalytics';
import ExpenseTracker from './components/ExpenseTracker';
import StaffAnalytics from './components/StaffAnalytics';
import ResetPassword from './components/ResetPassword';
import { Toaster } from "./components/ui/sonner";
import VerifyPage from './components/VerifyPage';

// ── Timing constants ────────────────────────────────────────────────────────
const INACTIVITY_WARN_MS  = 5 * 60 * 1000;  // 5 min  → show warning
const INACTIVITY_LIMIT_MS = 6 * 60 * 1000;  // 6 min  → hard logout
const ABSOLUTE_LIMIT_MS   = 24 * 60 * 60 * 1000; // 24 h  → forced logout
const SESSION_POLL_MS     = 30 * 1000;       // 30 s   → check for session supersede

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// JWT Token decoder
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

// ── Inactivity Warning Modal ─────────────────────────────────────────────────
function InactivityWarning({ secondsLeft, onContinue }) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Session Expiring Soon</h2>
              <p className="text-xs text-amber-100">You have been inactive</p>
            </div>
          </div>
        </div>
        {/* Body */}
        <div className="px-6 py-5 text-center space-y-4">
          <p className="text-sm text-gray-600">
            Your session will automatically end in
          </p>
          <div className="text-5xl font-bold text-orange-500 tabular-nums">
            {secondsLeft}
            <span className="text-2xl text-gray-400 font-normal ml-1">s</span>
          </div>
          <p className="text-xs text-gray-400">
            Click below to stay logged in
          </p>
          <button
            onClick={onContinue}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold text-sm hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-200"
          >
            Continue Session
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerified,      setIsVerified]      = useState(false);
  const [user,            setUser]            = useState(null);
  const [showWarning,     setShowWarning]     = useState(false);
  const [warningCountdown, setWarningCountdown] = useState(60); // seconds remaining in warning

  // Refs to hold timers without causing re-renders
  const lastActivityRef  = useRef(Date.now());
  const warnTimerRef     = useRef(null);
  const hardTimerRef     = useRef(null);
  const countdownRef     = useRef(null);
  const pollTimerRef     = useRef(null);
  const loginTimeRef     = useRef(null); // absolute login time

  // ── Core logout ────────────────────────────────────────────────────────────
  const handleLogout = useCallback((reason = 'Session expired. Please login again.') => {
    // Clear all timers
    [warnTimerRef, hardTimerRef, countdownRef, pollTimerRef].forEach(r => {
      if (r.current) { clearTimeout(r.current); clearInterval(r.current); r.current = null; }
    });
    setShowWarning(false);
    setIsAuthenticated(false);
    setIsVerified(false);
    setUser(null);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('login_timestamp');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('session_id');
    if (reason) toast.info(reason);
  }, []);

  // ── Schedule inactivity timers ──────────────────────────────────────────────
  const scheduleInactivityTimers = useCallback(() => {
    // Clear existing timers
    if (warnTimerRef.current)  { clearTimeout(warnTimerRef.current);  warnTimerRef.current  = null; }
    if (hardTimerRef.current)  { clearTimeout(hardTimerRef.current);  hardTimerRef.current  = null; }
    if (countdownRef.current)  { clearInterval(countdownRef.current); countdownRef.current  = null; }
    setShowWarning(false);

    const remainingWarnMs = INACTIVITY_WARN_MS;
    const remainingHardMs = INACTIVITY_LIMIT_MS;

    // 5-min: show warning
    warnTimerRef.current = setTimeout(() => {
      const countdown = Math.round((INACTIVITY_LIMIT_MS - INACTIVITY_WARN_MS) / 1000);
      setWarningCountdown(countdown);
      setShowWarning(true);
      // tick countdown every second
      countdownRef.current = setInterval(() => {
        setWarningCountdown(prev => {
          if (prev <= 1) { clearInterval(countdownRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
    }, remainingWarnMs);

    // 6-min: hard logout
    hardTimerRef.current = setTimeout(() => {
      handleLogout('Logged out due to inactivity.');
    }, remainingHardMs);

  }, [handleLogout]);

  // ── Activity detection ──────────────────────────────────────────────────────
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    localStorage.setItem('login_timestamp', Date.now().toString());
    if (showWarning) setShowWarning(false);
    scheduleInactivityTimers();
  }, [scheduleInactivityTimers, showWarning]);

  // ── Poll backend to detect session supersede ────────────────────────────────
  const startSessionPoll = useCallback(() => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) return;
      try {
        const res = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          const data = await res.json().catch(() => ({}));
          const msg = data.code === 'SESSION_SUPERSEDED'
            ? 'You have been logged out because your account was accessed from another device.'
            : 'Your session has expired. Please login again.';
          handleLogout(msg);
        }
      } catch {
        // network error — don't logout, will retry
      }
    }, SESSION_POLL_MS);
  }, [handleLogout]);

  // ── "Continue session" button ───────────────────────────────────────────────
  const handleContinueSession = useCallback(() => {
    resetActivity();
  }, [resetActivity]);

  // ── Auth setup on mount (restore session) ──────────────────────────────────
  useEffect(() => {
    const token      = localStorage.getItem('admin_token');
    const userData   = localStorage.getItem('admin_user');
    const loginTs    = localStorage.getItem('login_timestamp');

    if (!token || !userData || !loginTs) return;

    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp || decoded.exp * 1000 < Date.now()) {
      handleLogout('Your session has expired. Please login again.');
      return;
    }

    const loginTime = parseInt(loginTs, 10);
    if (Date.now() - loginTime > ABSOLUTE_LIMIT_MS) {
      handleLogout('Your 24-hour session has expired. Please login again.');
      return;
    }

    // Restore session
    loginTimeRef.current = loginTime;
    try {
      setUser(JSON.parse(userData));
      setIsAuthenticated(true);
      setIsVerified(true);
    } catch {
      handleLogout();
    }
  }, []); // eslint-disable-line

  // ── Start timers once authenticated ────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    const onActivity = () => resetActivity();
    ACTIVITY_EVENTS.forEach(e => document.addEventListener(e, onActivity, true));

    scheduleInactivityTimers();
    startSessionPoll();

    // Absolute 24-hour logout (time remaining from original login)
    const loginTime = loginTimeRef.current || Date.now();
    const remainingAbsolute = ABSOLUTE_LIMIT_MS - (Date.now() - loginTime);
    const absTimer = setTimeout(() => {
      handleLogout('Your 24-hour session has expired. Please login again.');
    }, Math.max(remainingAbsolute, 0));

    return () => {
      ACTIVITY_EVENTS.forEach(e => document.removeEventListener(e, onActivity, true));
      clearTimeout(absTimer);
      [warnTimerRef, hardTimerRef, countdownRef, pollTimerRef].forEach(r => {
        if (r.current) { clearTimeout(r.current); clearInterval(r.current); r.current = null; }
      });
    };
  }, [isAuthenticated]); // eslint-disable-line

  // ── Login handler ───────────────────────────────────────────────────────────
  const handleLogin = useCallback((loginData) => {
    if (!loginData?.token || !loginData?.user) return false;
    const now = Date.now();
    loginTimeRef.current = now;
    localStorage.setItem('admin_token',      loginData.token);
    localStorage.setItem('admin_user',       JSON.stringify(loginData.user));
    localStorage.setItem('login_timestamp',  now.toString());
    const decoded = decodeToken(loginData.token);
    localStorage.setItem('token_expiry', decoded?.exp ? String(decoded.exp * 1000) : String(now + ABSOLUTE_LIMIT_MS));
    if (loginData.sessionId) localStorage.setItem('session_id', loginData.sessionId);
    setUser(loginData.user);
    setIsAuthenticated(true);
    setIsVerified(true);
    toast.success(`Welcome back, ${loginData.user.name || 'User'}!`);
    return true;
  }, []);

  const toastConfig = {
    position: 'top-right',
    toastOptions: {
      style: { background: 'white', color: '#1f2937', border: '1px solid #e5e7eb' },
    },
  };

  return (
    <Router>
      {/* Inactivity warning overlay — rendered outside Routes so it always shows */}
      {isAuthenticated && showWarning && (
        <InactivityWarning
          secondsLeft={warningCountdown}
          onContinue={handleContinueSession}
        />
      )}

      <Routes>
        {/* ── Public routes – no auth required ── */}
        <Route path="/enquiry" element={
          <>
            <EnquiryForm />
            <Toaster {...toastConfig} />
          </>
        } />
        <Route path="/reset-password" element={
          <>
            <ResetPassword />
            <Toaster {...toastConfig} />
          </>
        } />

        {/* ── All other routes require authentication ── */}
        <Route path="*" element={
          !isAuthenticated ? (
            <>
              <LoginPage onLogin={handleLogin} />
              <Toaster style={{ zIndex: 99999 }} {...toastConfig} />
            </>
          ) : (
            <div className="App">
              <Layout onLogout={handleLogout} user={user}>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<Dashboard user={user} />} />
                  <Route path="/dashboard/lead-status/:tabValue" element={<LeadStatusPage />} />
                  <Route path="/courses" element={<CourseManagement />} />
                  <Route path="/students" element={<StudentManagement />} />
                  <Route path="/CRM" element={<CRMManagement />} />
                  <Route path="/students/:studentId" element={<StudentProfile />} />
                  <Route path="/trainer" element={<TrainerManagement user={user} />} />
                  <Route path="/fees" element={<FeeManagement />} />
                  <Route path="/attendance" element={<AttendanceManagement />} />

                  {/* Staff Management - Admin Only */}
                  <Route path="/staff" element={
                    user?.role === 'admin'
                      ? <StaffManagement user={user} />
                      : <Navigate to="/dashboard" replace />
                  } />

                  {/* Batch Analytics - Admin Only */}
                  <Route path="/analytics" element={
                    user?.role === 'admin'
                      ? <BatchAnalytics />
                      : <Navigate to="/dashboard" replace />
                  } />

                  <Route path="/expenses" element={
                    user?.role === 'admin'
                      ? <ExpenseTracker />
                      : <Navigate to="/dashboard" replace />
                  } />

                  {/* Staff Analytics - Admin Only */}
                  <Route path="/staff-analytics" element={
                    user?.role === 'admin'
                      ? <StaffAnalytics />
                      : <Navigate to="/dashboard" replace />
                  } />

                  <Route path="/verify" element={
                    isVerified
                      ? <Navigate to="/dashboard" replace />
                      : <VerifyPage onVerify={() => setIsVerified(true)} />
                  } />

                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Layout>
              <Toaster style={{ zIndex: 99999 }} {...toastConfig} />
            </div>
          )
        } />
      </Routes>
    </Router>
  );
}

export default App;